import { DeviceMobile, DownloadSimple, Export, PlusSquare, X } from '@phosphor-icons/react'
import { useState } from 'react'
import { AliensMark, Button, Modal, notify } from '@/design-system'
import { useT } from '@/i18n'
import { promptInstall, useInstallState } from '@/lib/pwa'

const DISMISS_KEY = 'aos.install.dismissedAt'
const readDismissed = () => {
  try {
    return Number(localStorage.getItem(DISMISS_KEY) || 0)
  } catch {
    return 0
  }
}
const writeDismissed = () => {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()))
  } catch {
    /* private mode */
  }
}

function useInstall() {
  const state = useInstallState()
  const [iosHelp, setIosHelp] = useState(false)
  const { locale } = useT()
  const ar = locale === 'ar'
  const install = async () => {
    if (state === 'ios') return setIosHelp(true)
    const ok = await promptInstall()
    if (ok) notify.success(ar ? 'تم تثبيت COREX كتطبيق' : 'COREX installed')
  }
  return { state, install, iosHelp, setIosHelp, ar }
}

function IosHelp({ open, onOpenChange, ar }: { open: boolean; onOpenChange: (o: boolean) => void; ar: boolean }) {
  const steps = ar
    ? [
        { icon: Export, text: 'اضغط زر المشاركة أسفل الشاشة في Safari' },
        { icon: PlusSquare, text: 'اختر "إضافة إلى الشاشة الرئيسية"' },
        { icon: DeviceMobile, text: 'افتح COREX من أيقونته مثل أي تطبيق' },
      ]
    : [
        { icon: Export, text: 'Tap the Share button in Safari' },
        { icon: PlusSquare, text: 'Choose "Add to Home Screen"' },
        { icon: DeviceMobile, text: 'Open COREX from its icon like any app' },
      ]
  return (
    <Modal open={open} onOpenChange={onOpenChange} title={ar ? 'تثبيت التطبيق على iPhone' : 'Install on iPhone'} size="sm">
      <ol className="space-y-3">
        {steps.map((s, i) => (
          <li key={i} className="flex items-center gap-3 text-sm">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-accent-soft text-accent">
              <s.icon size={20} weight="duotone" />
            </span>
            <span>
              <b className="me-1">{i + 1}.</b>
              {s.text}
            </span>
          </li>
        ))}
      </ol>
    </Modal>
  )
}

/** Top-bar button: shown only while the app can be installed and is not already running installed. */
export function InstallButton() {
  const { state, install, iosHelp, setIosHelp, ar } = useInstall()
  if (state === 'installed' || state === 'unavailable') return null
  return (
    <>
      <Button size="sm" variant="secondary" icon={DownloadSimple} onClick={install} className="hidden sm:inline-flex">
        {ar ? 'تثبيت التطبيق' : 'Install app'}
      </Button>
      <button type="button" onClick={install} className="rounded-md p-2 text-fg-muted hover:bg-surface-hover sm:hidden" aria-label={ar ? 'تثبيت التطبيق' : 'Install app'}>
        <DownloadSimple size={19} />
      </button>
      <IosHelp open={iosHelp} onOpenChange={setIosHelp} ar={ar} />
    </>
  )
}

/** A card on phones inviting to install; closing it hides it for two weeks. */
export function InstallBanner() {
  const { state, install, iosHelp, setIosHelp, ar } = useInstall()
  const [hidden, setHidden] = useState(() => Date.now() - readDismissed() < 14 * 86_400_000)
  if (hidden || state === 'installed' || state === 'unavailable') return null
  return (
    <>
      <div className="install-banner fixed inset-x-3 z-40 flex items-center gap-3 rounded-2xl border border-border bg-surface-raised p-3 shadow-lg md:hidden" role="dialog" aria-label={ar ? 'تثبيت التطبيق' : 'Install app'}>
        <span className="flex size-11 shrink-0 items-center justify-center rounded-xl" style={{ background: 'linear-gradient(140deg,#A855F7,#7C3AED 55%,#4C1D95)' }}>
          <AliensMark tone="white" className="h-5 w-auto" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-bold text-fg-strong">{ar ? 'ثبت COREX على هاتفك' : 'Install COREX'}</span>
          <span className="block text-xs text-fg-muted">{ar ? 'يفتح بسرعة دون متصفح مثل أي تطبيق' : 'Opens fast, full screen, like any app'}</span>
        </span>
        <Button size="sm" onClick={install}>
          {ar ? 'تثبيت' : 'Install'}
        </Button>
        <button
          type="button"
          onClick={() => {
            writeDismissed()
            setHidden(true)
          }}
          className="rounded-md p-1 text-fg-subtle"
          aria-label={ar ? 'إغلاق' : 'Close'}
        >
          <X size={16} />
        </button>
      </div>
      <IosHelp open={iosHelp} onOpenChange={setIosHelp} ar={ar} />
    </>
  )
}
